<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Upload de Arquivos DICOM</h4>
            
            <div class="row">
              <div class="col-lg-8">
                <div class="mb-3">
                  <label for="exam_id" class="form-label">ID do Exame (opcional)</label>
                  <input
                    id="exam_id"
                    v-model="examId"
                    type="text"
                    class="form-control"
                    placeholder="Digite o ID do exame para associar o DICOM"
                  />
                </div>

                <div class="mb-3">
                  <label for="dicom_files" class="form-label">Arquivos DICOM</label>
                  <input
                    id="dicom_files"
                    ref="fileInput"
                    type="file"
                    class="form-control"
                    multiple
                    accept=".dcm,.dicom"
                    @change="handleFileSelect"
                  />
                  <div class="form-text">
                    Selecione um ou mais arquivos DICOM (.dcm, .dicom)
                  </div>
                </div>

                <!-- File List -->
                <div v-if="selectedFiles.length > 0" class="mb-3">
                  <h6>Arquivos Selecionados:</h6>
                  <div class="list-group">
                    <div
                      v-for="(file, index) in selectedFiles"
                      :key="index"
                      class="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <i class="bx bx-file me-2"></i>
                        {{ file.name }}
                        <small class="text-muted ms-2">({{ formatFileSize(file.size) }})</small>
                      </div>
                      <button
                        class="btn btn-outline-danger btn-sm"
                        @click="removeFile(index)"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Upload Progress -->
                <div v-if="uploading" class="mb-3">
                  <div class="d-flex justify-content-between mb-2">
                    <span>Enviando arquivos...</span>
                    <span>{{ uploadProgress }}%</span>
                  </div>
                  <div class="progress">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="{ width: uploadProgress + '%' }"
                      :aria-valuenow="uploadProgress"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>

                <!-- Upload Results -->
                <div v-if="uploadResults.length > 0" class="mb-3">
                  <h6>Resultados do Upload:</h6>
                  <div class="list-group">
                    <div
                      v-for="(result, index) in uploadResults"
                      :key="index"
                      :class="[
                        'list-group-item',
                        result.success ? 'list-group-item-success' : 'list-group-item-danger'
                      ]"
                    >
                      <div class="d-flex justify-content-between">
                        <div>
                          <i :class="result.success ? 'bx bx-check-circle text-success' : 'bx bx-error-circle text-danger'"></i>
                          {{ result.filename }}
                        </div>
                        <div>
                          <span v-if="result.success" class="badge bg-success">
                            ID: {{ result.dicom_id }}
                          </span>
                          <span v-else class="badge bg-danger">
                            Erro
                          </span>
                        </div>
                      </div>
                      <div v-if="!result.success" class="text-danger small mt-1">
                        {{ result.error }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="selectedFiles.length === 0 || uploading"
                    @click="uploadFiles"
                  >
                    <i v-if="uploading" class="bx bx-loader bx-spin"></i>
                    <i v-else class="bx bx-upload"></i>
                    {{ uploading ? 'Enviando...' : 'Enviar Arquivos' }}
                  </button>
                  
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="clearFiles"
                    :disabled="uploading"
                  >
                    <i class="bx bx-trash"></i>
                    Limpar
                  </button>
                </div>
              </div>

              <div class="col-lg-4">
                <div class="card border">
                  <div class="card-body">
                    <h6 class="card-title">Instruções</h6>
                    <ul class="list-unstyled">
                      <li><i class="bx bx-check text-success"></i> Selecione arquivos DICOM (.dcm, .dicom)</li>
                      <li><i class="bx bx-check text-success"></i> Máximo de 100MB por arquivo</li>
                      <li><i class="bx bx-check text-success"></i> Múltiplos arquivos são suportados</li>
                      <li><i class="bx bx-check text-success"></i> Associe a um exame para melhor organização</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { technicianService } from "@/api/technician";

export default {
  page: {
    title: "Upload DICOM",
    meta: [{ name: "description", content: "Upload de arquivos DICOM" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Upload DICOM",
      items: [
        {
          text: "Técnico",
          href: "/technician",
        },
        {
          text: "Upload DICOM",
          active: true,
        },
      ],
      examId: "",
      selectedFiles: [],
      uploading: false,
      uploadProgress: 0,
      uploadResults: [],
    };
  },
  methods: {
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.selectedFiles = files;
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },

    clearFiles() {
      this.selectedFiles = [];
      this.uploadResults = [];
      this.$refs.fileInput.value = "";
    },

    async uploadFiles() {
      if (this.selectedFiles.length === 0) {
        this.$toast.error("Selecione pelo menos um arquivo");
        return;
      }

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadResults = [];

      try {
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i];
          
          try {
            const response = await technicianService.uploadDicom(file, this.examId);
            
            this.uploadResults.push({
              filename: file.name,
              success: true,
              dicom_id: response.data.dicom_id,
            });
          } catch (error) {
            this.uploadResults.push({
              filename: file.name,
              success: false,
              error: error.response?.data?.detail || "Erro no upload",
            });
          }

          // Update progress
          this.uploadProgress = Math.round(((i + 1) / this.selectedFiles.length) * 100);
        }

        const successCount = this.uploadResults.filter(r => r.success).length;
        const totalCount = this.uploadResults.length;

        if (successCount === totalCount) {
          this.$toast.success(`Todos os ${totalCount} arquivos foram enviados com sucesso!`);
        } else {
          this.$toast.warning(`${successCount} de ${totalCount} arquivos foram enviados com sucesso`);
        }

      } catch (error) {
        console.error("Erro durante o upload:", error);
        this.$toast.error("Erro durante o upload dos arquivos");
      } finally {
        this.uploading = false;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
  },
};
</script>
